import { AxiosInstance, AxiosResponse } from 'axios';
import React, { useContext, useState, FunctionComponent, PropsWithChildren, useEffect, useRef } from 'react';
import { IconButton, Link, Menu, MenuItem, Snackbar, Tab, Tabs, TextField, Typography } from '@material-ui/core'
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import { MoreVert } from '@material-ui/icons'
import Moment from 'react-moment';
import { logError } from 'src/utils/Error';
import '../assets/css/components/dataRow.css';
import 'src/assets/css/components/pipelineType.css'
import arrowRight from '../assets/images/svgs/arrow_left.svg';
import calendarIcon from '../assets/images/svgs/calendar.svg';
import dataIcon from '../assets/images/svgs/data.svg';
import startIcon from '../assets/images/svgs/play.svg';
import reloadIcon from '../assets/images/svgs/reload.svg';
import scheduleIcon from '../assets/images/svgs/schedule.svg';
import stopIcon from '../assets/images/svgs/square-icon.svg';
import manageIcon from '../assets/images/svgs/manage.svg';
import sparkleIcon from '../assets/images/svgs/sparkle.svg';
import { APIContext, diagnoseContextURI, workspacePipelinesURI } from '../contexts/api';
import { copyTextAsync } from '../utils/clipboard';
import { WorkspaceContext } from '../contexts/workspace';
import { bindContexts } from '../utils/bindContexts';
import { IDataRowProps } from 'src/models/components/IDataRow';
import { toMacro } from './DataScheduleComponent';
import JobRow from './JobRow';
import JobLogButton from 'src/components/JobLogButton'
import ConfirmDialog from 'src/components/ConfirmDialog'
import { WindowContext } from 'src/contexts/window';
import useRowStyles from 'src/assets/css/components/RowComponentsStyles';
import IPage from 'src/models/IPage'
import IJob, { JobStatus, JobType } from 'src/models/IJob'
import { statusLabel } from 'src/utils/jobStatus'
import { canDiscover, componentPluginType, isBackfillOf, pipelineType, pipelineTypeLabel } from 'src/utils/pipelineType'
import IPipeline, { IPipelineEnvironment, latestCheck, latestRun, PipelineHealthStatus, PipelineStatus, PipelineType } from 'src/models/IPipeline'
import DataPluginSettings from 'src/components/DataPluginSettings'
import copyIcon from 'src/assets/images/copyIcon.png'
import CopyToClipboard from 'react-copy-to-clipboard'
import chainIcon from '../assets/images/svgs/chain-icon.svg'
import Spinner from 'src/components/Spinner'
import CustomAccordion from './CustomAccordion';
import ImageHolder from 'src/components/ImageHolder'
import { ImageHolderSize } from 'src/models/components/IImageHolder'
import alerts from './../assets/images/sharewith/alerts.svg'
import allactivity from './../assets/images/sharewith/all_activity.svg'
import { SubscriptionType } from 'src/models/ISubscription'
import Chart from 'src/components/charts/Chart'
import ChoiceModal from 'src/components/ChoiceModal'
import PipelineDiagnoseModal from 'src/components/PipelineDiagnoseModal'
import BackfillDialog, { backfillProperties, IBackfillValues } from 'src/components/BackfillDialog'
import BackfillSection from 'src/components/BackfillSection'
import RunHistoryIndicator from 'src/components/RunHistoryIndicator'
import IngestionSplitDialog from 'src/components/IngestionSplitDialog'
import SchemaSection from 'src/components/SchemaSection'
import { sourceSchemas } from 'src/utils/schemaExport'
import assert from 'assert'

// Convert to ingestion is hidden for now: flip this to offer it again on workflows (IngestionSplitDialog is kept)
const CONVERT_TO_INGESTION_ENABLED = false

const DataRow: FunctionComponent<IDataRowProps> = (props: PropsWithChildren<IDataRowProps>) => {
    const classes = useRowStyles();
    const { catalogAPI } = useContext(APIContext);
    const api = catalogAPI as AxiosInstance;
    const { profile, workspace, pipelines, setPipelines } = useContext(WorkspaceContext);
    const { size } = useContext(WindowContext);
    const { renderLogs, openLog, downloadLog, handleOpenPipelineConfirmDialog } = props;

    // refresh pipeline to update status while job active
    const refreshReference = useRef<NodeJS.Timeout | null>(null)

    const [pipeline, setPipeline] = useState<IPipeline>(props.pipeline)
    const [latestJob, setLatestJob] = useState<IJob>()

    // the latest job of any kind is what is in flight, so a connection check still polls
    const isActiveJob = !!latestJob && [JobStatus.Queued, JobStatus.Running].includes(latestJob.status)
    // MEL-702: a connection check (Verify, Discover) is not a run, so the row's run status and its
    // history leave it out; a failed check shows as a notice of its own, as it needs the user's action
    const lastRun = latestRun(pipeline)
    const check = latestCheck(pipeline)
    const isChecking = !!check && [JobStatus.Queued, JobStatus.Running].includes(check.status)
    const hasFailedCheck = check?.status === JobStatus.Error
    // Diagnose is offered only when the latest run genuinely failed. A STOPPED run is
    // a manual/user-initiated cancel — the runs before it may have succeeded — so it is not a
    // failure to diagnose. WARNING is a failure with a retry still ahead of it (MEL-568), and
    // stays diagnosable: the user asked, so nothing is being pushed at them prematurely.
    const hasErrored = !!lastRun
        && [JobStatus.Error, JobStatus.Warning].includes(lastRun.status)
    const isDraft = pipeline.status === PipelineStatus.Draft
    const schedulesDisabled = !!workspace?.disablePipelineSchedules && !!pipeline.schedule
    // MEL-657: at least one of the last 5 scheduled runs failed. Only UNSTABLE is surfaced —
    // a stable pipeline gets no chip (nothing to act on), and UNKNOWN means there was no
    // scheduled run to judge, which must not be dressed up as either verdict.
    const isUnstable = pipeline.health?.status === PipelineHealthStatus.Unstable
    const isRunnable = !!pipeline._links['create job']
    const backfills: IPipeline[] = pipelines.filter((p: IPipeline) => isBackfillOf(pipeline, p))
        .sort((a: IPipeline, b: IPipeline) => b.created.localeCompare(a.created))
    // the tabs keep their values whichever are shown, so Streams sits before Backfills as the last value
    const backfillTab = 3
    const schemaTab = 4
    const isIngestion = pipelineType(pipeline) === PipelineType.Ingestion
    const hasSubscription = !!pipeline._links['delete subscription']
    const metricsLink = pipeline._links['metrics']

    const [state, setState] = useState({
        currentJobs: pipeline._links.jobs.href,
        nextJobs: '',
        prevJobs: '',
        jobsReady: false,
    });
    const [job, setJob] = useState<IJob>()

    // button state
    const [isChangingSubscription, setChangingSubscription] = useState(false)
    const [isRefreshing, setRefreshing] = useState(false)
    const [isStarting, setStarting] = useState(false)
    const [isStopping, setStopping] = useState(false)
    const [isExpanded, setExpanded] = useState(false)
    const [isMenuOpen, setMenuOpen] = useState(false)
    const menuAnchorEl = useRef<EventTarget & HTMLButtonElement>()
    const [isSubscribeDialogOpen, setSubscribeDialogOpen] = useState(false)
    const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false)
    const [isDiagnoseOpen, setDiagnoseOpen] = useState(false)
    const [backfillDialog, setBackfillDialog] = useState<{ source: IPipeline }>()
    const [isSplitOpen, setSplitOpen] = useState(false)
    const [isDiscoverRequested, setDiscoverRequested] = useState(false)
    // the specific run to diagnose (MEL-574 per-run diagnosis, opened from a JobRow dropdown);
    // undefined = the pipeline-level Diagnose button, which diagnoses the latest failure
    const [diagnoseJobId, setDiagnoseJobId] = useState<string | undefined>(undefined)
    // the backfill of the run to diagnose; undefined = this pipeline
    const [diagnoseBackfill, setDiagnoseBackfill] = useState<IPipeline>()
    // message for the run-dropdown prompt copy: "Preparing diagnosis prompt…" then "Copied to clipboard"
    const [promptCopyMessage, setPromptCopyMessage] = useState<string | null>(null)

    const [jobList, setJobList] = useState<IJob[]>()
    const [activeTab, setActiveTab] = useState(0)
    const [meltanoEnv, setMeltanoEnv] = useState({ display: "", copy: "" })
    const [isCopied, setCopied] = useState(false)

    const toggleCopiedAlert = () => setCopied(c => !c)

    const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setMenuOpen(true)
        menuAnchorEl.current = e.currentTarget
    }
    const handleCloseMenu = () => setMenuOpen(false)

    // Close the dropdown on scroll: with body scroll-lock disabled the page scrolls freely, but
    // MUI v4's popover positions once at open and doesn't follow its anchor, so it would float
    // detached from the button on scroll. Closing on scroll is the conventional menu behaviour and
    // keeps it from ever looking misplaced (MEL-574). Capture phase catches any ancestor container.
    useEffect(() => {
        if (!isMenuOpen) return
        const close = () => setMenuOpen(false)
        window.addEventListener('scroll', close, true)
        return () => window.removeEventListener('scroll', close, true)
    }, [isMenuOpen])

    const handleOpenJobConfirmDialog = (job: IJob) => {
        setConfirmDialogOpen(true)
        setJob(job)
    }
    const handleCloseJobConfirmDialog = () => setConfirmDialogOpen(false)

    const scheduleText = (schedule: any) => {
        if (!schedule)
            return 'Manual'
        if (toMacro(schedule) === "@hourly")
            return 'Hourly'
        if (toMacro(schedule) === "@daily")
            return 'Daily'
        return schedule
    }

    const addMarkerForJobStatus = (status: JobStatus) => {
        switch (status) {
            case JobStatus.Skipped:
                return 'grey'
            case JobStatus.Complete:
                return 'green'
            case JobStatus.Error:
                return 'red'
            case JobStatus.Warning:
                return 'warning'
            case JobStatus.Running:
                return 'runningGreen'
            case JobStatus.Stopped:
                return 'red'
            default:
                return 'orange'
        }
    }

    const setStateFromJobsResponse = (response: AxiosResponse<IPage<IJob>>) => {
        const page = response.data
        setJobList(page._embedded?.jobs)
        setState({
            ...state,
            currentJobs: page._links.self.href,
            nextJobs: page._links.next?.href || '',
            prevJobs: page._links.prev?.href || '',
            jobsReady: true,
        })
    }

    const getPipeline = async () => {
        await api.get(pipeline._links.self.href)
            // MEL-657: this refresh carries health, so the stability chip keeps up with the run
            // it is a verdict about — the 5s poll while a run is active ends on the response that
            // settles it, and that same response says whether the pipeline is now unstable. Health
            // is still absent from other payloads (the create/update responses; anything built by
            // the assembler without the controller attaching it), so keep carrying the last known
            // verdict forward rather than letting the chip vanish on a health-less refresh.
            .then((r: AxiosResponse<IPipeline>) => setPipeline(current => ({
                ...r.data,
                health: r.data.health || current.health,
            })))
            .catch(logError)
    }
    const verifyPipeline = async () => {
        await api.post(pipeline._links['verify pipeline'].href)
            .then(getPipeline)
            .catch(logError)
    }
    // MEL-702: find the streams again, without Verify's connection test - an ingestion's, or a workflow's with a tap and a target
    const discoverStreams = async () => {
        await api.post(pipeline._links['discover pipeline']!.href)
            .then(getPipeline)
            .catch(logError)
    }

    const getJobs = async () => {
        const [url, query] = state.currentJobs.split("?")

        const params = new URLSearchParams(query)
        params.set("size", "10")
        params.set("type", "PIPELINE_RUN")
        await api.get(`${url}?${params.toString()}`)
            .then(setStateFromJobsResponse)
            .catch(logError)
    }

    const createJob = async () => {
        await api.post(pipeline._links['create job'].href)
            .then(getPipeline)
            .catch(e => {
                setStarting(false)
                logError(e)
            })
    }

    const withdrawJob = async () => {
        await api.put(pipeline._links['withdraw job'].href)
            .then(getPipeline)
            .catch(e => {
                setStopping(false)
                logError(e)
            })
    }

    const getBackfills = () => api.get(workspacePipelinesURI(workspace._links.pipelines.href))
        .then((r: AxiosResponse<IPage<IPipeline>>) => {
            const read = (r.data._embedded?.pipelines || []).filter(p => isBackfillOf(pipeline, p))
            setPipelines((ps: IPipeline[]) => [...ps.filter(p => !isBackfillOf(pipeline, p)), ...read])
        })
        .catch(logError)

    const refreshHandler = async () => {
        setRefreshing(true)
        await Promise.all([getPipeline(), ...isExpanded && activeTab === backfillTab ? [getBackfills()] : []])
    }

    useEffect(() => {
        setLatestJob(pipeline._embedded?.['latest job'])
        setChangingSubscription(false)
        setRefreshing(false)
    }, [pipeline])

    // the jobs show in History only, but the tabs wait for a first read of them. Once read, a return to History
    // reads them again only for a pipeline read since
    const jobsReadFor = useRef<IPipeline>()
    useEffect(() => {
        if (isExpanded && (activeTab === 0 || !state.jobsReady) && jobsReadFor.current !== pipeline) {
            jobsReadFor.current = pipeline
            getJobs()
        }
    }, [pipeline, activeTab])

    // a stop is only requested, so the stop spins until a refresh finds the run ended
    useEffect(() => {
        if (!isActiveJob)
            setStopping(false)

        setStarting(false)
    }, [latestJob])

    useEffect(() => {
        isExpanded && getPipeline()
    }, [isExpanded])

    // a collapsed row stays mounted, so its chart reads again only for a pipeline read while the row is expanded
    const [metricsKey, setMetricsKey] = useState(pipeline)
    useEffect(() => {
        isExpanded && setMetricsKey(pipeline)
    }, [pipeline])

    useEffect(() => {
      // terminate any existing refresh interval
      if (refreshReference.current != null) {
        clearInterval(refreshReference.current as NodeJS.Timeout)
        refreshReference.current = null
      }
      // poll for pipeline status at 5s interval if the job is active, while the row's header or History shows it
      if (isActiveJob && (!isExpanded || activeTab === 0)) {
        refreshReference.current = setInterval(() => getPipeline(), 5000)
      }

      // return a cleanup function
      return () => {
        if (refreshReference.current != null) {
          clearInterval(refreshReference.current as NodeJS.Timeout)
          refreshReference.current = null
        }
      }
    }, [isActiveJob, isExpanded, activeTab])

    useEffect(() => {
        isConfirmDialogOpen && setMenuOpen(false)
    }, [isConfirmDialogOpen])

    useEffect(() => {
        if (activeTab !== 2) return

        !meltanoEnv.display && !meltanoEnv.copy && api.get(pipeline._links.environment.href).then((r: AxiosResponse<IPipelineEnvironment>) => {
            const parseEnvironment = (safe?: boolean) => Object.entries(r.data)
                .map(([k, v]) => `${k}=${safe && v.sensitive ? "***" : v.value}`)
                .join('\n')

            // generate display-safe and clipboard copy envs
            const display = parseEnvironment(true)
            const copy = parseEnvironment()

            setMeltanoEnv({ display: display, copy: copy })
        })

    }, [activeTab])

    const startStopHandler = async () => {
        if (isStarting || isStopping)
            return

        !jobList?.length && setState({ ...state, jobsReady: false })

        if (!isActiveJob) {
            setStarting(true)
            createJob()
        } else {
            setStopping(true)
            withdrawJob()
        }
    }

    const olderJobs = async () => {
        if (!state.nextJobs) return;

        await api.get(state.nextJobs)
            .then(setStateFromJobsResponse)
            .catch(logError)
    }

    const newerJobs = async () => {
        if (!state.prevJobs) return;

        await api.get(state.prevJobs)
            .then(setStateFromJobsResponse)
            .catch(logError)
    }

    useEffect(() => {
        if (!jobList)
            newerJobs()
    }, [jobList])

    const handleDeleteJob = async () => {
        if (!job || [JobStatus.Queued, JobStatus.Running].includes(job.status)) return

        await api.delete(job._links['delete job'].href)
            .then(getPipeline)
            .catch(logError);
    };

    const closeSubscribeDialog = () => setSubscribeDialogOpen(false)

    const handleSubscribe = async (subscriptionType: SubscriptionType) => {
        const addSubscriptionLink = pipeline._links['add subscription']
        assert(addSubscriptionLink, "Expected 'add subscription' link")

        closeSubscribeDialog()
        setChangingSubscription(true)

        await api.post(addSubscriptionLink.href, { type: subscriptionType })
        await getPipeline()
    }

    const handleUnsubscribe = async () => {
        const deleteSubscriptionLink = pipeline._links['delete subscription']
        assert(deleteSubscriptionLink, "Expected 'delete subscription' link")

        closeSubscribeDialog()
        setChangingSubscription(true)

        await api.delete(deleteSubscriptionLink.href)
        await getPipeline()
    }

    const handleWatch = () => {
        if (isChangingSubscription)
            return

        if (hasSubscription) {
            handleUnsubscribe()
            return
        }

        setMenuOpen(false)
        setSubscribeDialogOpen(true)
    }

    const watchBtn =
        <div
            className={[
                'button-wrapper',
                ...isChangingSubscription ? ['disabled'] : []
            ].join(' ')}
            onClick={handleWatch}
            title={hasSubscription ? "Unwatch" : "Watch"}>
            <span className='watch-action'>
                <Spinner active={isChangingSubscription}>
                    <i className={'fas ' + (hasSubscription ? 'fa-eye-slash' : 'fa-eye')} />
                </Spinner>
            </span>
        </div>

    const refreshBtn =
        <div
            className='button-wrapper'
            onClick={refreshHandler}
            title="Refresh">
            <span className='refresh-action'>
                <Spinner active={isRefreshing}>
                    <img src={reloadIcon} />
                </Spinner>
            </span>
        </div>

    const stopButton =
        <div
            className='button-wrapper'
            onClick={startStopHandler}
            title="Stop job">
            <span
                className='run-action'
                aria-label="Stop job">
                <Spinner active={isStopping}>
                    <img src={stopIcon}/>
                </Spinner>
            </span>
        </div>

    const startButton =
        <div
            className={[
                'button-wrapper',
                ...!isRunnable ? ['disabled'] : []
            ].join(' ')}
            title={isRunnable ? "Start job" : isDraft ? "Draft imports disabled" : "Import configuration incomplete"}>
            <span
                className='run-action'
                aria-label="Start job"
                onClick={startStopHandler}>
                <Spinner active={isStarting}>
                    <img src={startIcon}/>
                </Spinner>
            </span>
        </div>

    const arrowButton =
        <div
            className={`button-wrapper ${(size.smallScreen || size.mediumScreen) ? 'mobile' : ''} ${isExpanded ? 'collapse-center' : 'expand-center'}`}
            onClick={() => setExpanded(!isExpanded)}
            title={`${isExpanded ? 'Collapse' : 'Expand'}`}>
            <span className={`info-action ${isExpanded ? 'expanded' : ''}`}>
                <img src={arrowRight} />
            </span>
        </div>

    // The verdict is only as good as the history behind it, so say what it was based on rather
    // than implying a full window — a pipeline that has only run twice reads "1 of the last 2".
    // Deliberately "runs", not "scheduled runs": a manual trigger counts toward health too, so
    // naming the schedule here would misdescribe the number next to it. The count includes
    // attempts a retry went on to rescue (WARNING) — they failed, and the amber dots beside this
    // chip are where a user sees which ones they were.
    const unstableTitle = pipeline.health
        ? `${pipeline.health.failureCount} of the last ${pipeline.health.runsConsidered} `
            + `${pipeline.health.runsConsidered === 1 ? 'run' : 'runs'} failed`
        : undefined

    const type = pipelineType(pipeline)

    /*
     * MEL-702: what stops a draft ingestion running, e.g. dbt its pipeline file added. A workflow can
     * be converted into an ingestion here, by IngestionSplitDialog; an ingestion holding other tools
     * moves them to a workflow from its form's problems banner.
     */
    const problems = pipeline.problems || []
    const canModify = !!pipeline._links['update pipeline'] && !profile?.featuresDisabled?.includes("MODIFY_PIPELINE")
    const components = pipeline._embedded?.dataComponents || []
    // only a workflow with one source and one data store can become an ingestion: no point offering it to the rest
    const hasOneSourceAndStore = ['EXTRACTOR', 'LOADER'].every(t => components.filter(dc => componentPluginType(dc) === t).length === 1)
    // the app never writes an unmanaged workspace's repository, so its next deploy would undo the split
    // MEL-702: a pipeline that can discover its streams, or holds some already, has a schema to show
    const schemas = sourceSchemas(components)
    const hasSchema = canDiscover(type, components) || schemas.length > 0
    const splitLabel = CONVERT_TO_INGESTION_ENABLED
        && workspace?.managed !== false && type === PipelineType.Workflow && hasOneSourceAndStore
        ? 'Convert to ingestion' : undefined

    const runLabels = (job: IJob) => <>
        <span className={`inline-vertical-center pipeline-date circle ${addMarkerForJobStatus(job.status)}`}>{statusLabel(job.status)}</span>
        <span className={'pipeline-date'}>
            <Moment format="YYYY-MM-DD HH:mm" date={job.created} utc={true} local={true} />
            <img src={scheduleIcon} alt="schedule icon" />
            {job.startTime && job.endTime ?
                <Moment from={job.startTime}>{job.endTime}</Moment> :
                <Moment interval={1000} date={job.startTime} utc={true} durationFromNow={true} trim={true} format="h[h]m[m]s[s] [elapsed]" />}
        </span></>

    const labels = <>
        {/* first: what the pipeline is, before how it is doing (MEL-702) */}
        <span className={`inline-vertical-center pipeline-date pipeline-type ${type.toLowerCase()}`}>
            {pipelineTypeLabel(type)}
        </span>
        <span className='inline-vertical-center pipeline-date'>
            <img src={manageIcon} alt="pipeline status" />
            {pipeline.status}
        </span>
        {problems.length > 0 &&
            <span
                className='inline-vertical-center pipeline-date needs-fixing'
                title={problems.join('\n')}>
                <i className='fas fa-exclamation-circle' />
                Needs fixing
            </span>}
        {/* the workspace can turn schedules off (MEL-752): show the schedule, but not as if it runs */}
        <span
            className={'inline-vertical-center pipeline-date' + (schedulesDisabled ? ' schedule-disabled' : '')}
            data-testid='pipeline-schedule'
            title={schedulesDisabled ? 'Scheduled runs are off in this workspace' : undefined}>
            <img src={calendarIcon} alt="calendar icon" />
            {scheduleText(pipeline.schedule)}
        </span>
        {!isExpanded && lastRun && runLabels(lastRun)}
        {/* a Discover only finds the streams; a Verify tests the connection first (MEL-702) */}
        {isChecking && (check?.type === JobType.PipelineDiscover
            ? <span className='inline-vertical-center pipeline-date' data-testid='discovering-streams'>
                <i className='fas fa-search' />
                Discovering streams…
            </span>
            : <span className='inline-vertical-center pipeline-date' data-testid='checking-connection'>
                <i className='fas fa-plug' />
                Verifying connection…
            </span>)}
        {/*
          * The Unstable chip and, immediately after it, the run-history dots — both last in the
          * label row, after the pipeline's own state and latest run. The chip is a verdict over
          * history, so it reads after the facts it summarises; the dots are the evidence, and stay
          * adjacent to the chip when there is one.
          *
          * The chip shows only when UNSTABLE — a stable pipeline has nothing to act on. The dots,
          * though, now show for every pipeline that has run history, stable included: a row of green
          * ticks reassures at a glance that a pipeline is healthy, so a list scrolled top to bottom
          * reads as mostly green rather than only ever surfacing the red. (This reverses the earlier
          * unstable-only rule — the green dots turned out to be worth the ink.) UNKNOWN, a pipeline
          * with no runs to judge, still shows neither: RunHistoryIndicator renders null on an empty
          * history.
          */}
        {isUnstable &&
            <span
                className='inline-vertical-center pipeline-date unstable'
                title={unstableTitle}>
                <i className='fas fa-exclamation-triangle' />
                Unstable
            </span>}
        <RunHistoryIndicator runs={pipeline.health?.recentRuns || []} />
    </>

    const iconButton = (
        <div className={`${(size.smallScreen || size.mediumScreen) ? 'actions-menu-mobile' : ''}`}>
            <IconButton
                className={classes.button}
                aria-label='actions-menu'
                onClick={handleOpenMenu}
                title="More">
                <MoreVert className={classes.largeIcon} />
            </IconButton>
        </div>
    );


    const handleEditPipeline = () => props.onEdit(pipeline)

    const handleVerifyPipeline = async () => {
        setStarting(true)
        handleCloseMenu()
        await verifyPipeline()
    }

    const handleBackfillPipeline = async () => {
        handleCloseMenu()

        // the pipelines list omits the properties and the streams, so read the whole pipeline
        await api.get(pipeline._links.self.href)
            .then(source => setBackfillDialog({ source: source.data }))
            .catch(logError)
    }

    const runBackfill = async (values: IBackfillValues) => {
        const saved: IPipeline = await api.post(pipeline._links['backfill pipeline'].href, values).then(r => r.data)
        setPipelines((ps: IPipeline[]) => [...ps, saved])
        setExpanded(true)
        setActiveTab(backfillTab)
    }

    // a new backfill has no state, so its run starts again from its start date. The backfill is as `GET pipeline` returns it
    const restartBackfill = async (backfill: IPipeline) => {
        const source: IPipeline = await api.get(pipeline._links.self.href).then(r => r.data)
        const values: IBackfillValues = {
            properties: backfillProperties(source, backfill),
            timeout: backfill.timeout,
            maxRetries: backfill.maxRetries,
        }
        const saved: IPipeline = await api.post(pipeline._links['backfill pipeline'].href, values, { params: { replace: backfill.id } })
            .then(r => r.data)
        setPipelines((ps: IPipeline[]) => [...ps.filter(p => p.id !== backfill.id), saved])
    }

    const handleDiscoverStreams = async () => {
        setStarting(true)
        setDiscoverRequested(true)
        handleCloseMenu()
        await discoverStreams()
        // the discovery is now the latest check, which says whether it is still running
        setDiscoverRequested(false)
    }

    const handleDeletePipeline = () => {
        handleCloseMenu()
        handleOpenPipelineConfirmDialog(pipeline)
    }

    const handleChangeTab = (event: React.ChangeEvent<unknown>, tab: number) => setActiveTab(tab)

    // MEL-574 per-run diagnosis: open the shared modal scoped to a specific failed run from the
    // history, reusing the whole diagnose flow (streaming states, copy, re-run) unchanged.
    const handleDiagnoseJob = (targetJob: IJob, backfill?: IPipeline) => {
        setDiagnoseBackfill(backfill)
        setDiagnoseJobId(targetJob.id)
        setDiagnoseOpen(true)
    }

    // A prompt fetch started early (on dropdown open) so it is usually already resolved by the time
    // the user clicks "Copy diagnosis prompt" — keyed by jobId so a stale prepare is ignored.
    const preparedPrompt = useRef<{ jobId: string; promise: Promise<string> } | null>(null)

    const fetchJobPrompt = (targetJob: IJob, backfill?: IPipeline): Promise<string> =>
        api.get(diagnoseContextURI(workspace!.id, (backfill ?? pipeline).id, targetJob.id))
            .then((r: AxiosResponse<{ prompt?: string }>) => r.data?.prompt || '')
            .catch((e: unknown) => { logError(e); return '' })

    // MEL-574: pre-fetch the prompt when the run's Diagnose dropdown opens, so the copy is instant.
    const handlePrepareJobPrompt = (targetJob: IJob, backfill?: IPipeline) => {
        if (!workspace?.id)
            return
        preparedPrompt.current = { jobId: targetJob.id, promise: fetchJobPrompt(targetJob, backfill) }
    }

    // "Copy diagnosis prompt" from the run dropdown: copy the assembled prompt (the same
    // /diagnose/context the modal uses, needs no Claude key) without opening the modal. Uses the
    // pre-fetched promise when available; the clipboard write is initiated synchronously with the
    // pending fetch (copyTextAsync) so nothing is lost while the request is in flight. Shows a
    // "Preparing…" toast only if the copy is actually slow, then confirms with "Copied".
    const handleCopyJobPrompt = (targetJob: IJob, backfill?: IPipeline) => {
        if (!workspace?.id)
            return
        const prepared = preparedPrompt.current
        const pending = prepared?.jobId === targetJob.id ? prepared.promise : fetchJobPrompt(targetJob, backfill)

        let settled = false
        // only surface a loading toast if the prompt is not ready within a beat — avoids a
        // "Preparing…" flash when the pre-fetch already made it instant
        const preparingTimer = setTimeout(() => {
            if (!settled) setPromptCopyMessage('Preparing diagnosis prompt…')
        }, 250)

        copyTextAsync(pending)
            // same confirmation wording as the modal's "Copy diagnosis prompt" (MEL-574 — one message
            // for both copy-prompt entry points)
            .then(() => setPromptCopyMessage('Prompt copied to clipboard'))
            // replace (not leave) the non-auto-hiding "Preparing…" toast on failure, so it never sticks
            .catch((e: unknown) => { logError(e); setPromptCopyMessage('Could not copy the prompt — please try again') })
            .finally(() => { settled = true; clearTimeout(preparingTimer) })
    }

    const handleCloseDiagnose = () => {
        setDiagnoseOpen(false)
        // clear the run scope so the next pipeline-level Diagnose defaults back to the latest failure
        setDiagnoseJobId(undefined)
        setDiagnoseBackfill(undefined)
    }

    const diagnoseButton = (onClick: () => void) =>
        <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<img src={sparkleIcon} alt="" style={{ width: 13, height: 12 }} />}
            onClick={onClick}
            style={{
                marginLeft: 'auto',
                backgroundColor: '#F2EEFC',
                color: '#7C56DC',
                textTransform: 'none',
                fontWeight: 600,
            }}
            title="Diagnose why this pipeline failed with Melty AI">
            Diagnose
        </Button>

    // a failed connection check is not a failed run, but it needs the user: their settings, most likely
    const failedCheckNotice = (failed: IJob) =>
        <Alert
            severity='warning'
            data-testid='failed-check'
            className='failed-check'
            action={<>
                <Button size='small' color='inherit' onClick={handleEditPipeline}>Edit Pipeline</Button>
                <JobLogButton job={failed} data-testid='failed-check-log' />
                <Button size='small' color='inherit' onClick={() => handleDiagnoseJob(failed)}>Diagnose</Button>
            </>}>
            {failed.type === JobType.PipelineDiscover
                ? 'Couldn’t find this pipeline’s streams. '
                : 'Couldn’t connect with this pipeline’s settings. '}
            Check its settings and save them to try again.
        </Alert>

    const buttons = (
        <div className='inline-vertical-center'>
            {backfillDialog &&
                <BackfillDialog
                    {...backfillDialog}
                    onClose={() => setBackfillDialog(undefined)}
                    onRun={runBackfill} />}
            <PipelineDiagnoseModal
                open={isDiagnoseOpen}
                onClose={handleCloseDiagnose}
                pipeline={diagnoseBackfill ?? pipeline}
                workspaceId={workspace?.id}
                jobId={diagnoseJobId} />
            {isSplitOpen &&
                <IngestionSplitDialog
                    open={isSplitOpen}
                    pipeline={pipeline}
                    // refresh this row only: the list reloads when the dialog closes
                    onDone={getPipeline}
                    onClose={() => setSplitOpen(false)} />}
            {!(size.smallScreen || size.mediumScreen) && <>
                {watchBtn}
                {refreshBtn}
                {isActiveJob ? stopButton : startButton}
                {arrowButton}
                {iconButton}</>}
            <Menu
                className={classes.dropdownMenu}
                // keep the page scrollable while this dropdown is open (MUI locks body scroll by
                // default) — matches the per-run diagnose menu (MEL-574)
                disableScrollLock
                open={isMenuOpen}
                anchorEl={menuAnchorEl.current}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                onClose={handleCloseMenu}>
                {(size.smallScreen || size.mediumScreen) &&
                    <MenuItem
                        className={classes.dropdownItem}
                        dense
                        onClick={handleWatch}>
                        <Spinner active={isChangingSubscription}>
                            <i className={'fas ' + (hasSubscription ? 'fa-eye-slash' : 'fa-eye')} />
                            <span>{hasSubscription ? "Unwatch" : "Watch"}</span>
                        </Spinner>
                    </MenuItem>}
                {(size.smallScreen || size.mediumScreen) &&
                    <MenuItem
                        className={classes.dropdownItem}
                        dense
                        onClick={refreshHandler}>
                        <Spinner active={isRefreshing}>
                            <i className='fas fa-redo' />
                            <span>Refresh</span>
                        </Spinner>
                    </MenuItem>}
                {(size.smallScreen || size.mediumScreen) &&
                    <MenuItem
                        className={classes.dropdownItem}
                        dense
                        disabled={!isRunnable && !isActiveJob}
                        onClick={startStopHandler}>
                        <Spinner active={isStarting || isStopping}>
                            <i className={'fas ' + (isActiveJob ? 'fa-stop' : 'fa-play')} />
                            <span>{isActiveJob ? "Stop job" : "Start job"}</span>
                        </Spinner>
                    </MenuItem>}
                <MenuItem
                    className={classes.dropdownItem}
                    dense
                    disabled={!pipeline._links['update pipeline']}
                    onClick={handleEditPipeline}>
                    <i className='fas fa-edit' />
                    <span>Edit</span>
                </MenuItem>
                {canModify && splitLabel &&
                <MenuItem
                    className={classes.dropdownItem}
                    dense
                    disabled={isActiveJob}
                    onClick={() => { handleCloseMenu(); setSplitOpen(true) }}>
                    <i className='fas fa-random' />
                    <span>{splitLabel}</span>
                </MenuItem>}
                <MenuItem
                    className={classes.dropdownItem}
                    dense
                    disabled={!pipeline._links['verify pipeline'] || isStarting || isActiveJob}
                    onClick={handleVerifyPipeline}>
                    <i className='fas fa-play' />
                    <span>Verify</span>
                </MenuItem>
                {canDiscover(type, components) &&
                <MenuItem
                    className={classes.dropdownItem}
                    dense
                    disabled={!pipeline._links['discover pipeline'] || isStarting || isActiveJob}
                    onClick={handleDiscoverStreams}>
                    <i className='fas fa-search' />
                    <span>Discover Streams</span>
                </MenuItem>}
                {isIngestion &&
                <MenuItem
                    className={classes.dropdownItem}
                    dense
                    disabled={!pipeline._links['backfill pipeline']}
                    onClick={handleBackfillPipeline}>
                    <i className='fas fa-history' />
                    <span>Backfill</span>
                </MenuItem>
                }
                {!profile?.featuresDisabled?.includes("MODIFY_PIPELINE") &&
                <MenuItem
                    className={classes.dropdownItem}
                    dense
                    disabled={!pipeline._links['delete pipeline']}
                    onClick={handleDeletePipeline}>
                    <i className='fas fa-trash' />
                    <span>Delete</span>
                </MenuItem>
                }
            </Menu>
        </div>
    )

    const metricsDataset = {
        visualisation: `
            {
                "chartjs-chart": {
                    "chartType": "bar",
                    "options": {
                        "responsive": true,
                        "maintainAspectRatio": false
                    }
                }
            }`,
        metadata: `
            {
                "name": "metrics",
                "label": "Job Metrics",
                "related_table": {
                    "columns": [
                        {
                            "name": "job-created",
                            "label": "Created",
                            "description": "Job Created"
                        }
                    ],
                    "aggregates": [
                        {
                            "name": "value",
                            "label": "Rows",
                            "description": "Total Rows"
                        }
                    ]
                }
            }`,
        _links: {
            data: metricsLink
        },
    }

    return <>
        <ChoiceModal
            message="Watch for..."
            isOpen={isSubscribeDialogOpen}
            onClose={closeSubscribeDialog}
        >
            <div className='item'>
                <Button
                    variant='text'
                    onClick={() => handleSubscribe(SubscriptionType.Alerts)}
                    className="modal-button">
                    <img src={alerts} alt='icon' />
                    <div>Errors</div>
                </Button>
            </div>
            <div className='item'>
                <Button
                    variant='text'
                    onClick={() => handleSubscribe(SubscriptionType.All)}
                    className="modal-button">
                    <img src={allactivity} alt='icon' />
                    <div>All activity</div>
                </Button>
            </div>
        </ChoiceModal>
        <ConfirmDialog
            isOpen={isConfirmDialogOpen}
            title="Delete job?"
            message={`Remove this job from the ${type === PipelineType.Ingestion ? 'import' : 'workflow'}. This action cannot be undone.`}
            cancelLabel="Close"
            confirmLabel="Delete"
            handleClose={handleCloseJobConfirmDialog}
            handleConfirm={handleDeleteJob} />
        <Snackbar
            open={!!promptCopyMessage}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            // "Preparing…" stays until the copy resolves; any terminal message (copied / error) auto-hides
            autoHideDuration={promptCopyMessage === 'Preparing diagnosis prompt…' ? null : 2000}
            onClose={() => setPromptCopyMessage(null)}
            message={promptCopyMessage} />
        <Snackbar
            open={isCopied}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            autoHideDuration={3000}
            onClose={toggleCopiedAlert}>
            <Alert severity='success'>Copied</Alert>
        </Snackbar>
        <div
            key={pipeline.id}
            aria-label={pipeline.label || pipeline.name}
            className="dataRow-container">
            <div className={`${isExpanded ? "row-info-container" : "row-info-container collapse-pipeline-datastore"}`}>
                <div className='pipeline-row-data'>
                    <div className="pipeline-row-info">
                        <h2 className='pipeline-row-name'>{pipeline.label || pipeline.name}</h2>
                        {buttons}
                {(size.smallScreen || size.mediumScreen) ? iconButton : null}
                    </div>
                    <div className="data-source-store image-holders">
                        {pipeline._embedded?.dataComponents.map(dc => {
                            const defaultIcon = dc.dataPluginType === 'LOADER' ? dataIcon : chainIcon

                            return <ImageHolder
                                key={dc.id}
                                imageUrl={dc.dataPluginLogoUrl || defaultIcon}
                                size={ImageHolderSize.Small}/>})}
                    </div>
                    <div className='row-labels'>
                        {labels}
                        {hasErrored && diagnoseButton(() => { setDiagnoseJobId(undefined); setDiagnoseOpen(true) })}
                    </div>
                    {hasFailedCheck && check && failedCheckNotice(check)}
                </div>
                {(size.smallScreen || size.mediumScreen) ? arrowButton : null}
            </div>
            <div id={pipeline.id} className={`data-jobs-container ${isExpanded ? 'd-block' : 'd-none'}`} >
                <div data-testid="jobs-spinner" style={ state.jobsReady ? { "display": 'none' } : {}} className="content-center">
                    <Spinner active={true}>&nbsp;</Spinner>
                </div>
                {state.jobsReady && (
                    <>
                        <Tabs
                            indicatorColor='primary'
                            value={activeTab}
                            onChange={handleChangeTab}>
                            <Tab label="History" value={0} />
                            <Tab label="Settings" value={1} />
                            {hasSchema && <Tab label="Streams" value={schemaTab} />}
                            <Tab label="Environment" value={2} />
                            {isIngestion && <Tab label="Backfills" value={backfillTab} />}
                        </Tabs>
                        {activeTab === schemaTab &&
                            <SchemaSection
                                pipelineName={pipeline.name}
                                sources={schemas}
                                schemaHref={pipeline._links['schema']?.href}
                                refreshKey={JSON.stringify([schemas, pipeline.properties])}
                                canDiscover={!!pipeline._links['discover pipeline'] && !isActiveJob}
                                isDiscovering={isChecking || isDiscoverRequested}
                                onDiscover={handleDiscoverStreams} />}
                        {activeTab === backfillTab && (backfills.length
                            ? backfills.map(backfill => <BackfillSection
                                key={backfill.id}
                                backfill={backfill}
                                metricsDataset={{ ...metricsDataset, _links: { data: backfill._links['metrics'] } }}
                                jobMarker={addMarkerForJobStatus}
                                runLabels={runLabels}
                                renderLogs={renderLogs}
                                openLog={openLog}
                                downloadLog={downloadLog}
                                onRestart={pipeline._links['backfill pipeline'] && restartBackfill}
                                onDelete={handleOpenPipelineConfirmDialog}
                                diagnoseButton={diagnoseButton}
                                onDiagnose={job => handleDiagnoseJob(job, backfill)}
                                onCopyPrompt={job => handleCopyJobPrompt(job, backfill)}
                                onPrepare={job => handlePrepareJobPrompt(job, backfill)}
                                isShown={isExpanded && activeTab === backfillTab} />)
                            : <Typography className="datastore-properties" color="textSecondary">
                                {isDraft
                                    ? 'Publish the pipeline to backfill it.'
                                    : <span>No backfills yet{pipeline._links['backfill pipeline'] && <> - <Link
                                        component='button'
                                        variant='body1'
                                        className='schema-discover-link'
                                        onClick={handleBackfillPipeline}>create a backfill</Link></>}</span>}
                            </Typography>)}
                        {activeTab === 0 && <>
                            {metricsLink && <div data-testid="metrics-chart" className="metrics-chart">
                                <Chart dataset={metricsDataset} refreshKey={metricsKey} />
                            </div>}
                            {jobList && <>
                                {jobList.map(job =>
                                    <JobRow
                                        key={job.id}
                                        job={job}
                                        jobMarker={addMarkerForJobStatus}
                                        renderLogs={renderLogs}
                                        openLog={openLog}
                                        handleOpenJobConfirmDialog={handleOpenJobConfirmDialog}
                                        downloadLog={downloadLog}
                                        onDiagnose={handleDiagnoseJob}
                                        onCopyPrompt={handleCopyJobPrompt}
                                        onPrepare={handlePrepareJobPrompt}
                                    />)}
                                <div className="pagination-buttons">
                                    <button disabled={!state.prevJobs} onClick={newerJobs}>
                                        <img
                                            className="arrow-left"
                                            src={arrowRight}
                                            title="Newer jobs"
                                            alt="Newer jobs" />
                                        Newer
                                    </button>
                                    <button disabled={!state.nextJobs} onClick={olderJobs}>
                                        Older
                                        <img
                                            className="arrow-right"
                                            src={arrowRight}
                                            title="Older jobs"
                                            alt="Older jobs" />
                                    </button>
                                </div></>}</>}
                        {activeTab === 1 &&
                            <div className='datastore-properties'>
                                {pipeline._embedded?.dataComponents.map((dc, i) =>
                                    <CustomAccordion
                                        key={dc.id}
                                        title={dc.name}
                                        isLast={i === pipeline._embedded!.dataComponents.length  - 1}
                                        defaultExpanded={i === 0}>
                                        <DataPluginSettings
                                            selectedDataplugin={dc._embedded.dataplugin}
                                            prefix={dc.name}
                                            properties={pipeline.properties}
                                            readOnlyProperties={dc.properties}
                                            handleCopyProp={toggleCopiedAlert} />
                                    </CustomAccordion>)}
                            </div>}
                        {activeTab === 2 &&
                            <div className='datastore-properties'>
                                <CopyToClipboard
                                    text={meltanoEnv.copy}
                                    onCopy={toggleCopiedAlert}>
                                    <TextField
                                        id={`${pipeline.id}-env`}
                                        className='pointer'
                                        multiline
                                        variant='outlined'
                                        value={meltanoEnv.display}
                                        label=".env"
                                        InputProps={{
                                            style: { alignItems: 'flex-start' },
                                            readOnly: true,
                                            endAdornment:
                                                <img src={copyIcon} width={20} height={20} />
                                        }} />
                                </CopyToClipboard>
                            </div>}
                    </>)}
            </div>
        </div >
    </>

};

export const DataRowWithContext = bindContexts(DataRow, [APIContext, WorkspaceContext]);

export default DataRow;
